import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookMarked } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import { Combobox } from '@/shared/components/ui/combobox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { listActiveCuentasContables } from '@/shared/api/controllers/cuentasContables';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { reclasificarCuentaContable } from '../api';

/**
 * Estructura de la caja, no imputación contable real: Apertura/Cierre y las
 * patas de Transferencia/Depósito. Espejo de `CATEGORIAS_ESTRUCTURALES` en
 * `server/src/routes/tesoreria.ts` — el server corta igual, esto sólo evita
 * ofrecer un botón que va a fallar.
 */
export const CATEGORIAS_SIN_CUENTA_CONTABLE = new Set([
  // Estructura de la caja.
  'Apertura',
  'Cierre',
  'Transferencia',
  'Deposito',
  // Clave de los automatismos Nova / Inter-Co: el server las rechaza con
  // `blocked_automatismo` porque reimputarlas corta el circuito que las busca
  // por `categoria`. Espejo de `CATEGORIAS_DE_AUTOMATISMO` en
  // `server/src/routes/tesoreria.ts`.
  'Inter-co Nova',
  'Pago Inter-Co',
  'Cobro Inter-Co',
  'Comision Caja',
  'Comision Nova',
  'Comision MercadoPago',
  'Margen Nova',
  'Transferencia Caja Diferencia',
]);

/** ¿Este movimiento admite reimputación? Vale para cierres ya cerrados. */
export function puedeReclasificar(args: {
  categoria: string | null | undefined;
  status: string | null | undefined;
}): boolean {
  if ((args.status ?? '').trim() === 'Anulado') return false;
  return !CATEGORIAS_SIN_CUENTA_CONTABLE.has((args.categoria ?? '').trim());
}

export type MovimientoAReclasificar = {
  id: string | number;
  categoria: string | null;
  cuentaContable: string | null;
  descripcion?: string | null;
  /** Sólo para mostrar contexto: qué cierre/caja se está tocando. */
  contexto?: string | null;
  fecha?: string | null;
  /** Ingreso / Egreso / Apertura de Caja … */
  tipo?: string | null;
  /** Ya formateado por quien llama — el importe no se toca acá. */
  montoTexto?: string | null;
};

type CuentaOpcion = {
  key: string;
  cuenta: string;
  categoria: string;
};

/**
 * Cambiar la imputación contable de un movimiento ya asentado. Se usa desde
 * Tesorería y desde el preview del reporte de Cierres de Caja — el mismo
 * diálogo en los dos lados, así que la regla del par no puede divergir.
 *
 * El operador elige una CUENTA del catálogo y la categoría viene pegada: en la
 * base, `cash_movements` se une a `gl_accounts` por el par de texto
 * (categoría, cuenta contable) y no por FK, así que elegirlas por separado
 * permitiría armar un par inexistente y la fila desaparecería del Estado
 * Financiero. El server revalida el par igual.
 */
export function ReclasificarCuentaDialog({
  mov,
  onClose,
  onDone,
}: {
  mov: MovimientoAReclasificar;
  onClose: () => void;
  /** Se dispara después de un cambio efectivo, para refrescar la pantalla. */
  onDone?: () => void;
}) {
  const qc = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const [seleccion, setSeleccion] = useState<CuentaOpcion | null>(null);
  const [motivo, setMotivo] = useState('');

  const cuentasQ = useQuery({
    queryKey: ['cuentas-contables', activeCountry.code, 'activas'],
    queryFn: listActiveCuentasContables,
    staleTime: 5 * 60_000,
  });

  const opciones = useMemo<CuentaOpcion[]>(() => {
    const out: CuentaOpcion[] = [];
    for (const c of cuentasQ.data ?? []) {
      const cuenta = (c.fields.CuentaContable_CC ?? '').trim();
      const categoria = (c.fields.Categoria_CC ?? '').trim();
      // Sin categoría el par no existe en el catálogo y el server lo va a
      // rechazar: no la ofrecemos.
      if (!cuenta || !categoria) continue;
      out.push({ key: `${categoria}||${cuenta}`, cuenta, categoria });
    }
    // Mismo orden que Configuración → Cuentas Contables: la CUENTA es el
    // título y la CATEGORÍA la aclaración. La cuenta se repite a propósito
    // (53 cuentas activas sobre 41 nombres: de "Gastos de Oficina" cuelgan
    // cinco categorías), así que se ordena por cuenta y después por categoría
    // para que las repetidas queden juntas y se distingan por el renglón de
    // abajo — igual que en la grilla del catálogo.
    return out.sort(
      (a, b) =>
        a.cuenta.localeCompare(b.cuenta, 'es') ||
        a.categoria.localeCompare(b.categoria, 'es'),
    );
  }, [cuentasQ.data]);

  // Mismo orden que el selector y que el catálogo: cuenta, después categoría.
  const actual = [mov.cuentaContable, mov.categoria].filter(Boolean).join(' · ') || '—';
  const sinCambio =
    seleccion != null &&
    seleccion.categoria === (mov.categoria ?? '') &&
    seleccion.cuenta === (mov.cuentaContable ?? '');

  const mut = useMutation({
    mutationFn: () =>
      reclasificarCuentaContable(mov.id, {
        categoria: seleccion!.categoria,
        cuentaContable: seleccion!.cuenta,
        motivo: motivo.trim() || undefined,
      }),
    onSuccess: (r) => {
      if (r.outcome === 'updated') {
        toast.success('Cuenta contable actualizada', {
          description: `${r.categoria} / ${r.cuentaContable}`,
        });
        // El cambio mueve el Estado Financiero y los reportes de caja, no
        // sólo la grilla que se está mirando.
        qc.invalidateQueries({ queryKey: ['tesoreria'] });
        qc.invalidateQueries({ queryKey: ['reportes'] });
        onDone?.();
        onClose();
        return;
      }
      if (r.outcome === 'sin_cambios') {
        toast.info('El movimiento ya estaba imputado a esa cuenta');
        onClose();
        return;
      }
      toast.error('No se pudo reclasificar', { description: r.message });
    },
    onError: (err) => {
      toast.error('No se pudo reclasificar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && !mut.isPending && onClose()}>
      <DialogContent
        className="max-w-md"
        processing={mut.isPending && 'Reclasificando…'}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
              <BookMarked className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>Cambiar cuenta contable</DialogTitle>
              <DialogDescription>
                Sólo cambia la imputación: el importe, la caja y la fecha del
                movimiento quedan igual.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="rounded-lg border bg-secondary px-3 py-2 text-sm">
            {mov.descripcion && (
              <div className="mb-1 truncate font-medium text-foreground" title={mov.descripcion}>
                {mov.descripcion}
              </div>
            )}
            {(mov.fecha || mov.tipo) && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">{mov.tipo || 'Movimiento'}</span>
                <span className="font-medium text-foreground">{mov.fecha || '—'}</span>
              </div>
            )}
            {mov.montoTexto && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Importe</span>
                <span className="font-medium tabular-nums text-foreground">
                  {mov.montoTexto}
                </span>
              </div>
            )}
            <div className="flex items-start justify-between gap-3">
              <span className="shrink-0 text-muted-foreground">Imputación actual</span>
              <span className="text-right font-medium text-foreground">{actual}</span>
            </div>
            {mov.contexto && (
              <div className="mt-1 text-xs text-muted-foreground">{mov.contexto}</div>
            )}
          </div>

          <div className="space-y-1">
            <Label className="text-sm">Nueva cuenta contable</Label>
            <Combobox
              value={seleccion}
              onChange={setSeleccion}
              items={opciones}
              getKey={(o) => o.key}
              getLabel={(o) => o.cuenta}
              getSublabel={(o) => o.categoria}
              matcher={(o, q) =>
                o.cuenta.toLowerCase().includes(q) || o.categoria.toLowerCase().includes(q)
              }
              placeholder={cuentasQ.isPending ? 'Cargando…' : 'Buscar categoría o cuenta'}
              disabled={mut.isPending || cuentasQ.isPending}
            />
            {/* El Combobox cerrado sólo pinta `getLabel`, o sea la cuenta. Y
                la cuenta SE REPITE entre categorías ("Comisones Ventas" está
                en Fuerza de Venta Externa y en Interna), así que sin esto el
                operador no puede saber cuál de las dos eligió antes de
                guardar. Confirmamos el par elegido, no un texto de ayuda. */}
            {seleccion ? (
              <p className="text-xs text-muted-foreground">
                Se va a imputar a{' '}
                <span className="font-medium text-foreground">{seleccion.cuenta}</span>
                {' · '}
                <span className="font-medium text-foreground">{seleccion.categoria}</span>
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Se elige el par completo: arriba la cuenta contable, abajo la
                categoría. Una misma cuenta puede tener varias categorías.
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="motivo-reclasificacion" className="text-sm">
              Motivo <span className="text-muted-foreground">(opcional)</span>
            </Label>
            <Textarea
              id="motivo-reclasificacion"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              disabled={mut.isPending}
              rows={2}
              maxLength={500}
              placeholder="Por qué se reimputa"
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => mut.mutate()}
            disabled={mut.isPending || !seleccion || sinCambio}
            title={sinCambio ? 'Es la misma cuenta que ya tiene' : undefined}
            className="gap-2 bg-sky-600 text-white hover:bg-sky-700"
          >
            <BookMarked className="h-4 w-4" aria-hidden />
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
